import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "@mui/material";

import { UnfollowPopup } from "components/UI/Popups/Unfollow";
import NicheActionsPopup from "components/UI/Popups/NicheActionsPopup";
import { hideAllMenus, showAuthModal } from "store/application/application-action";
import { useMyNichesQuery, useSingleNiche } from "queries";
import { apiNiches } from "services";
import { isAuthenticated } from "services/Auth";
import { NicheInfo } from "components/niches/NicheInfo";
import { NicheGifList } from "./NicheGifList";
import { LoadingNotice } from "components/blocks";
import { Image } from "components";

import "./NichePage.scss";

import moreRoundIcon from "components/icons/moreRoundIcon.svg";
import DEFAULT_BACKGROUND from "components/icons/DEFAULT_NICHE_BACKGROUND.png";
import { INicheList } from "services/types";

const DELETED_NICHE_CODE = "NicheDeletedException";
const NICHE_NOT_FOUND_CODE = "NicheNotFoundException";

export const NichePage = () => {
  const { id } = useParams();

  const niche = useSingleNiche(id);
  const myFollowedNiches = useMyNichesQuery({ previews: true }).data;
  const [isFollowing, setFollowing] = useState<boolean|undefined>(undefined);
  const [followedNiches, setFollowedNiches] = useState<INicheList>();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isNicheActionsOpen, setIsNicheActionsOpen] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(hideAllMenus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (followedNiches && followedNiches.niches && niche && niche.data) {
      if (followedNiches.niches.find(el => el.id === niche.data?.id)) {
        setFollowing(true);
      }
    }
  }, [followedNiches, niche]);

  useEffect(() => {
    if (myFollowedNiches) {
      setFollowedNiches(myFollowedNiches);
    }
  }, [myFollowedNiches]);

  const handleFollow = () => {
    isAuthenticated().then((isLoggedIn) => {
      if (isLoggedIn) {
        const nicheId = niche.data?.id;

        if (nicheId !== undefined) {
          apiNiches.follow(nicheId).then(() => {
            console.debug(`[niches] Sent a follow request for niche ${nicheId}`);
            setFollowing(true);
          });
        }
      } else {
        dispatch(showAuthModal());
      }
    });
  };

  const handleUnfollowButton = () => {
    if (isFollowing) {
      setIsPopupOpen(true);
    }
  };

  const handleUnfollow = () => {
    const nicheId = niche.data?.id;

    if (nicheId !== undefined) {
      apiNiches.unfollow(nicheId).then(() => {
        console.debug(`[niches] Sent an unfollow request for niche ${nicheId}`);
        setFollowing(false);
        setIsPopupOpen(false);
      });
    }
  };

  const togglePopup = () => {
    setIsPopupOpen(prev => !prev);
  };

  const toggleNicheActions = () => {
    setIsNicheActionsOpen(prev => !prev);
  };

  if (niche.isLoading) {
    return <LoadingNotice />;
  }

  // @ts-ignore
  if (niche?.error?.response?.data?.error?.code === DELETED_NICHE_CODE) {
    return <p className="nichePage-isDeleted">Niche deleted, nothing to see here.</p>;
  }

  // @ts-ignore
  if (niche?.error?.response?.data?.error?.code === NICHE_NOT_FOUND_CODE) {
    return <p className="nichePage-isDeleted">Niche not found, nothing to see here.</p>;
  }

  if (!niche.isSuccess || niche.data === undefined) {
    return <p className="errorMessage">Error loading the page, please refresh.</p>;
  }

  if (isFollowing === null) {
    // Initialize the local following state from the API.
    setFollowing(niche.data.following || false);
  }

  const coverImage = niche.data.cover;

  return (
    <>
      <div className="nicheCover">
        <Image
          className="stripe"
          src={coverImage}
          fallback={DEFAULT_BACKGROUND}
        />

        <div className="shadowGradient" />
      </div>

      <div className="wide page nichePage">
        <UnfollowPopup
          isOpen={isPopupOpen}
          togglePopup={togglePopup}
          handleUnfollow={handleUnfollow}
        />

        <NicheActionsPopup
          isOpen={isNicheActionsOpen}
          togglePopup={toggleNicheActions}
        />

        <div className="background">
          {niche.data.id === "disabled" && <Button onClick={toggleNicheActions} className="nicheAction">
            <img src={moreRoundIcon} alt="more" style={{
              height: "auto",
              width: "5vw",
            }} />
          </Button>}
        </div>

        {niche.isSuccess && <NicheInfo
          niche={niche.data}
          following={isFollowing}
          follow={handleFollow}
          unfollow={handleUnfollowButton}
        />}

        {id && <NicheGifList id={id} />}
      </div>
    </>
  );
};
